/**
 * Format message date for display in group headers
 */
export function formatMessageDate(dateKey: string): string {
  if (dateKey === 'today') {
    return 'Today';
  } else if (dateKey === 'yesterday') {
    return 'Yesterday';
  }

  // For weekday or specific dates, return as is but capitalized
  return dateKey.charAt(0).toUpperCase() + dateKey.slice(1);
}

/**
 * Format message timestamp for display
 */
export function formatMessageTime(dateString: string): string {
  const date = new Date(dateString);
  const now = new Date();
  const diffInSeconds = Math.floor((now.getTime() - date.getTime()) / 1000);

  // Just now (less than a minute)
  if (diffInSeconds < 60) {
    return 'Just now';
  }

  // Minutes ago (less than an hour)
  if (diffInSeconds < 3600) {
    const minutes = Math.floor(diffInSeconds / 60);
    return `${minutes}m ago`;
  }

  // Today - show time
  if (isToday(date)) {
    return formatTime(date);
  }

  // Yesterday
  if (isYesterday(date)) {
    return `Yesterday ${formatTime(date)}`;
  }

  // Within a week - show weekday and time
  if (isWithinWeek(date)) {
    const weekday = date.toLocaleDateString('en-US', { weekday: 'short' });
    return `${weekday} ${formatTime(date)}`;
  }

  // Older - show date and time
  return formatFullDate(date);
}

/**
 * Format a friendly date and time string for conversation events
 */
export function formatFriendlyDateTime(dateString?: string | null): string {
  if (!dateString) {
    return '';
  }

  const date = new Date(dateString);

  if (Number.isNaN(date.getTime())) {
    return '';
  }

  const now = new Date();
  const diffInSeconds = Math.floor((now.getTime() - date.getTime()) / 1000);

  if (diffInSeconds < 60 && diffInSeconds >= 0) {
    return 'Just now';
  }

  if (diffInSeconds < 3600 && diffInSeconds >= 0) {
    const minutes = Math.floor(diffInSeconds / 60);
    return `${minutes}m ago`;
  }

  if (isToday(date)) {
    return `Today at ${formatTime(date)}`;
  }

  if (isYesterday(date)) {
    return `Yesterday at ${formatTime(date)}`;
  }

  if (isWithinWeek(date)) {
    const weekday = date.toLocaleDateString('en-US', { weekday: 'long' });
    return `${weekday} at ${formatTime(date)}`;
  }

  const options: Intl.DateTimeFormatOptions = {
    month: 'short',
    day: 'numeric',
  };

  if (date.getFullYear() !== now.getFullYear()) {
    options.year = 'numeric';
  }

  const formattedDate = date.toLocaleDateString('en-US', options);
  return `${formattedDate} at ${formatTime(date)}`;
}

/**
 * Format time in 12-hour format
 */
function formatTime(date: Date): string {
  return date
    .toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
    })
    .toLowerCase();
}

/**
 * Format full date
 */
function formatFullDate(date: Date): string {
  const now = new Date();
  const options: Intl.DateTimeFormatOptions = {
    month: 'short',
    day: 'numeric',
  };

  if (date.getFullYear() !== now.getFullYear()) {
    options.year = 'numeric';
  }

  return date.toLocaleDateString('en-US', options);
}

/**
 * Check if date is today
 */
function isToday(date: Date): boolean {
  const today = new Date();
  return date.toDateString() === today.toDateString();
}

/**
 * Check if date is yesterday
 */
function isYesterday(date: Date): boolean {
  const yesterday = new Date();
  yesterday.setDate(yesterday.getDate() - 1);
  return date.toDateString() === yesterday.toDateString();
}

/**
 * Check if date is within a week
 */
function isWithinWeek(date: Date): boolean {
  const oneWeekAgo = new Date();
  oneWeekAgo.setDate(oneWeekAgo.getDate() - 7);
  return date > oneWeekAgo;
}

/**
 * Get relative time string (e.g., "2 hours ago", "3 days ago")
 */
export function getRelativeTime(dateString: string): string {
  const date = new Date(dateString);
  const now = new Date();
  const diffInMs = now.getTime() - date.getTime();
  const diffInSeconds = Math.floor(diffInMs / 1000);
  const diffInMinutes = Math.floor(diffInSeconds / 60);
  const diffInHours = Math.floor(diffInMinutes / 60);
  const diffInDays = Math.floor(diffInHours / 24);

  if (diffInSeconds < 60) {
    return 'just now';
  } else if (diffInMinutes < 60) {
    return `${diffInMinutes} ${diffInMinutes === 1 ? 'minute' : 'minutes'} ago`;
  } else if (diffInHours < 24) {
    return `${diffInHours} ${diffInHours === 1 ? 'hour' : 'hours'} ago`;
  } else if (diffInDays < 7) {
    return `${diffInDays} ${diffInDays === 1 ? 'day' : 'days'} ago`;
  } else {
    return formatFullDate(date);
  }
}

/**
 * Format a date string for display (e.g., "March 15, 2024")
 */
export function formatDate(dateString: string): string {
  const date = new Date(dateString);
  return date.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });
}

/**
 * Format a timestamp (milliseconds since epoch) into a simple relative format
 * Suitable for AI query timestamps and other compact time displays
 */
export function formatTimestampSimple(timestamp: number): string {
  if (!timestamp) return '';

  const date = new Date(timestamp);
  const now = new Date();
  const diffInSeconds = Math.floor((now.getTime() - date.getTime()) / 1000);
  const diffInMinutes = Math.floor(diffInSeconds / 60);
  const diffInHours = Math.floor(diffInMinutes / 60);
  const diffInDays = Math.floor(diffInHours / 24);

  // Just now (< 1 minute)
  if (diffInSeconds < 60) {
    return 'Just now';
  }

  // Minutes ago (< 1 hour)
  if (diffInMinutes < 60) {
    return `${diffInMinutes}m ago`;
  }

  // Hours ago (< 24 hours)
  if (diffInHours < 24) {
    return `${diffInHours}h ago`;
  }

  // Yesterday
  if (diffInDays === 1) {
    return 'Yesterday';
  }

  // Within a week (2-6 days ago)
  if (diffInDays < 7) {
    return `${diffInDays} days ago`;
  }

  // Older - use existing formatFullDate logic
  return formatFullDate(date);
}

/**
 * Check if two dates are on different days
 */
export function isNewDay(date1: string | Date, date2: string | Date): boolean {
  const d1 = typeof date1 === 'string' ? new Date(date1) : date1;
  const d2 = typeof date2 === 'string' ? new Date(date2) : date2;
  return d1.toDateString() !== d2.toDateString();
}

/**
 * Format date for message group headers
 */
export function formatDateHeader(dateString: string): string {
  const date = new Date(dateString);
  const now = new Date();

  // Today
  if (isToday(date)) {
    return 'Today';
  }

  // Yesterday
  if (isYesterday(date)) {
    return 'Yesterday';
  }

  // Within a week - show weekday
  if (isWithinWeek(date)) {
    return date.toLocaleDateString('en-US', { weekday: 'long' });
  }

  // Same year - omit year
  if (date.getFullYear() === now.getFullYear()) {
    return date.toLocaleDateString('en-US', {
      month: 'long',
      day: 'numeric',
    });
  }

  // Different year - show full date
  return date.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });
}
